import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { classNames } from 'primereact/utils';
import { useSelector } from 'react-redux';

import { HeaderProgram } from './utils/utils';
import { loginLuca, getLucaInventoryPeriodList, getLucaInventoryPeriodDetail, sendReceiptEnvanter } from '../module/luca/utils/lucaRequest';
import Loading from '../Loading';

const extractDataArray = (response) => {
    const candidates = [response?.data?.data?.data?.data, response?.data?.data?.data, response?.data?.data, response?.data];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
        if (Array.isArray(candidate?.data)) return candidate.data;
    }

    return [];
};

const extractResponseMessage = (response) => response?.data?.data?.info || response?.data?.data?.success || response?.data?.data?.message || response?.data?.message || '';

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const amount = Number(value);
    if (Number.isNaN(amount)) return '-';

    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
};

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const amount = Number(value);
    if (Number.isNaN(amount)) return '-';

    return new Intl.NumberFormat('tr-TR').format(amount);
};

const inventoryUnitLabels = {
    TNE: 'ton',
    BX: 'Kutu',
    LTR: 'lt',
    C62: 'Adet',
    KGM: 'kg',
    KJO: 'kJ',
    GRM: 'g',
    MGM: 'mg',
    NT: 'Net Ton',
    GT: 'Gross Ton',
    MTR: 'm',
    MMT: 'mm',
    KTM: 'km',
    MLT: 'ml',
    MMQ: 'mm3',
    CLT: 'cl',
    CMK: 'cm2',
    CMQ: 'cm3',
    CMT: 'cm',
    DMK: 'dm2',
    MTK: 'm2',
    MTQ: 'm3',
    DAY: 'Gün',
    MON: 'Ay',
    PA: 'Paket',
    KWH: 'KWH',
    MWH: 'MWH',
    ANN: 'Yıl',
    HUR: 'Saat',
    D61: 'Dakika',
    D62: 'Saniye',
    CCT: 'Ton baş.taşıma kap.',
    D30: 'Brüt kalori',
    D40: '1000 lt',
    LPA: 'saf alkol lt',
    B32: 'kg.m2',
    NCL: 'hücre adet',
    PR: 'Çift',
    R9: '1000 m3',
    SET: 'Set',
    T3: '1000 adet',
    Q37: 'SCM',
    Q39: 'NCM',
    J39: 'mmBTU',
    G52: 'CM3',
    DZN: 'Düzine',
    CT: 'Karat'
};

const formatInventoryUnit = (unitCode) => inventoryUnitLabels[String(unitCode || '').trim()] || unitCode || '';

const formatQuantity = (value, unitCode) => {
    const unitLabel = formatInventoryUnit(unitCode);
    return [formatNumber(value), unitLabel].filter(Boolean).join(' ');
};

const getStatusSeverity = (statusText) => {
    const normalizedStatus = String(statusText || '').toLocaleLowerCase('tr-TR');

    if (normalizedStatus.includes('kaydedildi')) return 'success';
    if (normalizedStatus.includes('hazırlanıyor')) return 'warning';
    if (normalizedStatus.includes('hata')) return 'danger';
    return 'info';
};

export default function LucaInventoryPage() {
    const toast = useRef(null);
    const { selectedCompany } = useSelector((state) => state.userCompany);
    const { isAuthenticatedWithVkn, taxNumber } = useSelector((state) => state.auth);

    const [selectedCompanyCode, setSelectedCompanyCode] = useState(null);
    const [periodOptions, setPeriodOptions] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [inventoryPeriods, setInventoryPeriods] = useState([]);
    const [selectedInventoryPeriodId, setSelectedInventoryPeriodId] = useState(null);
    const [selectedInventoryPeriod, setSelectedInventoryPeriod] = useState(null);
    const [inventoryDetails, setInventoryDetails] = useState([]);
    const [inventoryDetailGlobalFilter, setInventoryDetailGlobalFilter] = useState(null);
    const [inventoryExpandedRows, setInventoryExpandedRows] = useState(null);
    const [inventoryLoading, setInventoryLoading] = useState(false);
    const [inventoryDetailLoading, setInventoryDetailLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendDialogVisible, setSendDialogVisible] = useState(false);
    const [enteredBy, setEnteredBy] = useState('');

    const selectedModule = useMemo(() => selectedCompany?.modules?.find((module) => module.title === 'E-Defter'), [selectedCompany]);
    const lucaCompanies = useMemo(() => selectedModule?.setting?.settings?.program?.lucaCompanies || [], [selectedModule]);
    const lucaCompanyOptions = useMemo(() => lucaCompanies.map((company) => ({ label: company.companyName, value: company.companyCode })), [lucaCompanies]);
    const selectedLucaCompany = useMemo(() => lucaCompanies.find((company) => String(company.companyCode) === String(selectedCompanyCode)), [lucaCompanies, selectedCompanyCode]);

    const inventoryPeriodOptions = useMemo(
        () =>
            inventoryPeriods.map((period) => ({
                label: period.aciklama || period.tarih || `Envanter dönemi ${period.id}`,
                value: String(period.id),
                period
            })),
        [inventoryPeriods]
    );

    const inventoryDetailRows = useMemo(() => inventoryDetails.map((item, index) => ({ ...item, _rowKey: `${item.envanterDonemId || 'detail'}-${item.id || index}-${index}` })), [inventoryDetails]);
    const sendEnabled = !!selectedInventoryPeriod && inventoryDetails.length > 0 && !inventoryDetailLoading && !sendLoading;
    const pageLoading = inventoryLoading || inventoryDetailLoading;

    useEffect(() => {
        if (!isAuthenticatedWithVkn || !taxNumber || lucaCompanies.length === 0) return;

        const company = lucaCompanies.find((item) => String(item.taxId) === String(taxNumber));
        if (company) setSelectedCompanyCode(company.companyCode);
    }, [isAuthenticatedWithVkn, taxNumber, lucaCompanies]);

    useEffect(() => {
        setInventoryPeriods([]);
        setSelectedInventoryPeriodId(null);
        setSelectedInventoryPeriod(null);
        setInventoryDetails([]);
        setInventoryExpandedRows(null);
        setInventoryDetailGlobalFilter(null);
        setSendDialogVisible(false);
        setEnteredBy('');

        if (!selectedLucaCompany?.periodId) {
            setPeriodOptions([]);
            setSelectedPeriodId(null);
            return;
        }

        setPeriodOptions([{ label: String(selectedLucaCompany.periodTitle || selectedLucaCompany.periodId), value: String(selectedLucaCompany.periodId) }]);
        setSelectedPeriodId(String(selectedLucaCompany.periodId));
    }, [selectedLucaCompany]);

    const getFreshLucaSession = async () => {
        const req = selectedModule?.setting?.settings?.program?.requirements?.[0];

        if (!req?.username || !req?.password || !req?.customerNumber) {
            throw new Error('Luca giriş bilgileri bulunamadı.');
        }

        const response = await loginLuca(req.username, req.password, req.customerNumber);
        const session = response?.data?.data;

        if (!session?.jSessionId || !session?.wMonId) {
            throw new Error('Luca oturumu alınamadı.');
        }

        return session;
    };

    const resetInventorySelection = () => {
        setInventoryPeriods([]);
        setSelectedInventoryPeriodId(null);
        setSelectedInventoryPeriod(null);
        setInventoryDetails([]);
        setInventoryExpandedRows(null);
        setInventoryDetailGlobalFilter(null);
        setSendDialogVisible(false);
        setEnteredBy('');
    };

    const handleCompanyChange = (event) => {
        setSelectedCompanyCode(event.value);
    };

    const handleLoadInventoryPeriods = async () => {
        if (!selectedLucaCompany || !selectedPeriodId) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen Luca şirketi ve dönem seçiniz.', life: 3000 });
            return;
        }

        setInventoryLoading(true);
        resetInventorySelection();

        try {
            const session = await getFreshLucaSession();
            const response = await getLucaInventoryPeriodList(session.jSessionId, session.wMonId, String(selectedPeriodId), String(selectedLucaCompany.companyCode));
            const data = extractDataArray(response);
            const responseMessage = extractResponseMessage(response);

            setInventoryPeriods(data);
            if (data.length > 0) {
                await handleLoadInventoryDetail(data[0], { showToast: false });
            }
            toast.current.show({
                severity: data.length > 0 ? 'success' : 'info',
                summary: data.length > 0 ? 'Başarılı' : 'Bilgi',
                detail: responseMessage || `${data.length} adet envanter dönemi getirildi.`,
                life: 3000
            });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.message || error?.response?.data?.message || 'Envanter dönemleri getirilemedi.', life: 3000 });
        } finally {
            setInventoryLoading(false);
        }
    };

    const handleLoadInventoryDetail = async (period, options = {}) => {
        const { showToast = true } = options;
        if (!selectedLucaCompany || !selectedPeriodId || !period?.id) return;

        setSelectedInventoryPeriod(period);
        setSelectedInventoryPeriodId(String(period.id));
        setInventoryDetailLoading(true);
        setInventoryDetails([]);
        setInventoryExpandedRows(null);

        try {
            const session = await getFreshLucaSession();
            const response = await getLucaInventoryPeriodDetail(session.jSessionId, session.wMonId, String(selectedPeriodId), String(selectedLucaCompany.companyCode), String(period.id));
            const data = extractDataArray(response);

            setInventoryDetails(data);
            if (showToast) {
                toast.current.show({
                    severity: data.length > 0 ? 'success' : 'info',
                    summary: data.length > 0 ? 'Başarılı' : 'Bilgi',
                    detail: data.length > 0 ? `${data.length} adet envanter kaydı getirildi.` : 'Seçilen dönem için envanter kaydı bulunamadı.',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.message || error?.response?.data?.message || 'Envanter detayı getirilemedi.', life: 3000 });
        } finally {
            setInventoryDetailLoading(false);
        }
    };

    const handleInventoryPeriodChange = (event) => {
        const option = inventoryPeriodOptions.find((item) => item.value === event.value);

        setSelectedInventoryPeriodId(event.value);
        setSelectedInventoryPeriod(option?.period || null);
        setInventoryDetails([]);
        setInventoryExpandedRows(null);
        setInventoryDetailGlobalFilter(null);

        if (option?.period) {
            handleLoadInventoryDetail(option.period);
        }
    };

    const handleSubmitSend = async (directSendValue) => {
        if (!enteredBy.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen gönderen kişi bilgisini giriniz.', life: 3000 });
            return;
        }

        const webServiceReq = selectedLucaCompany?.webService?.requirements;
        if (!selectedLucaCompany?.webService?.url || !webServiceReq?.username || !webServiceReq?.password) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Web servis bilgileri bulunamadı.', life: 3000 });
            return;
        }

        setSendLoading(true);
        try {
            const payload = {
                entered_by: enteredBy.trim(),
                companyId: String(selectedLucaCompany.companyCode),
                periodId: String(selectedPeriodId),
                lucaCompany: selectedLucaCompany,
                webServiceUrl: selectedLucaCompany.webService.url,
                webServiceUsername: webServiceReq.username,
                webServicePassword: webServiceReq.password,
                inventoryPeriod: selectedInventoryPeriod,
                directSend: directSendValue,
                data: inventoryDetails
            };

            const response = await sendReceiptEnvanter(payload);
            toast.current.show({ severity: 'success', summary: 'Başarılı', detail: response?.data?.message || 'Envanter gönderildi.', life: 3000 });
            setSendDialogVisible(false);
            setEnteredBy('');
        } catch (error) {
            error.code === 'ERR_NETWORK'
                ? toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İstek zaman aşımına uğradı', life: 3000 })
                : toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || error?.message || 'Envanter gönderilemedi.', life: 3000 });
        } finally {
            setSendLoading(false);
        }
    };

    const statusTemplate = (rowData) => <Tag className="text-white" value={rowData.durumAciklama || '-'} severity={getStatusSeverity(rowData.durumAciklama)} />;

    const selectedInventoryPeriodInfo = selectedInventoryPeriod ? (
        <div className="flex flex-wrap align-items-center gap-2 mt-2">
            <Tag className="text-white" value={selectedInventoryPeriod.durumAciklama || '-'} severity={getStatusSeverity(selectedInventoryPeriod.durumAciklama)} />
            <Tag className="text-white" value={selectedInventoryPeriod.defterOlusmus ? 'Defter Oluşmuş' : 'Defter Oluşmamış'} severity={selectedInventoryPeriod.defterOlusmus ? 'success' : 'warning'} />
            {selectedInventoryPeriod.tarih && <span className="text-sm text-color-secondary">{selectedInventoryPeriod.tarih}</span>}
        </div>
    ) : null;

    const inventoryInfoTemplate = (rowData) => (
        <div className="flex flex-column gap-1">
            <span className="font-bold">{rowData.kebir}</span>
            <span>{rowData.kebirAdi}</span>
            <span className="text-color-secondary text-sm">{rowData.aciklama}</span>
        </div>
    );

    const inventoryMethodTemplate = (rowData) => (
        <div className="flex flex-column gap-1 text-sm">
            <span>{rowData.yontemAciklama || '-'}</span>
            <span className="text-color-secondary">Detay: {rowData.detaylar?.length || 0}</span>
        </div>
    );

    const amountTemplate = (rowData) => (
        <div className="flex flex-column gap-1 text-sm">
            <span>
                <strong>TL:</strong> {formatCurrency(rowData.tlTutar)}
            </span>
            <span>
                <strong>Döviz:</strong> {formatNumber(rowData.dovizTutar)} {rowData.dovizCinsi || ''}
            </span>
        </div>
    );

    const detailExpansionTemplate = (rowData) => (
        <div className="p-3 flex flex-column gap-2" style={{ backgroundColor: 'var(--surface-ground)' }}>
            {(rowData.detaylar || []).length === 0 ? (
                <span className="text-color-secondary">Envanter detayı bulunamadı</span>
            ) : (
                rowData.detaylar.map((detail, index) => (
                    <div key={`${detail.id || index}-${detail.hesapKodu || index}`} className="surface-card border-1 surface-border border-round p-3" style={{ boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)' }}>
                        <div className="grid align-items-start">
                            <div className="col-12 md:col-4">
                                <div className="text-xs text-color-secondary mb-1">Hesap</div>
                                <div className="font-semibold">{detail.hesapKodu}</div>
                                <div className="text-sm">{detail.hesapAdi}</div>
                            </div>
                            <div className="col-6 md:col-2">
                                <div className="text-xs text-color-secondary mb-1">TL Tutar</div>
                                <div className="font-semibold">{formatCurrency(detail.tlTutar)}</div>
                            </div>
                            <div className="col-6 md:col-2">
                                <div className="text-xs text-color-secondary mb-1">Miktar</div>
                                <div className="font-semibold">{formatQuantity(detail.miktar, detail.birim)}</div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="text-xs text-color-secondary mb-1">Ürün</div>
                                <div className="font-semibold">{detail.urunKod || '-'}</div>
                                <div className="text-sm">{detail.urunAciklama || '-'}</div>
                            </div>
                        </div>
                        {detail.aciklama && <div className="text-sm text-color-secondary mt-2 pt-2 border-top-1 surface-border">{detail.aciklama}</div>}
                    </div>
                ))
            )}
        </div>
    );

    const detailHeader = (
        <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
            <div className="flex flex-column gap-1">
                <h4 className="m-0">Envanter Kayıtları</h4>
                {selectedInventoryPeriod && <span className="text-sm text-color-secondary">{selectedInventoryPeriod.aciklama}</span>}
            </div>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setInventoryDetailGlobalFilter(e.target.value)} placeholder="Ara..." />
            </span>
        </div>
    );

    return (
        <div>
            <Toast ref={toast} />
            {pageLoading ? (
                <div className="w-full" style={{ height: 'calc(100vh - 8rem)' }}>
                    <Loading loading={pageLoading} />
                </div>
            ) : (
                <>
                    <div className="flex align-items-center justify-content-between">
                        <HeaderProgram selectedCompany={selectedCompany} moduleName="E-Defter" />
                    </div>
                    <h3 className="mb-5">Luca Envanter</h3>

                    <div className="card flex align-items-start justify-content-between flex-wrap gap-4 py-4">
                        <div className="flex align-items-start gap-4 flex-wrap">
                            <div className="w-20rem relative">
                                <label className="mb-2 block">Şirketler</label>
                                <Dropdown
                                    value={selectedCompanyCode}
                                    onChange={handleCompanyChange}
                                    options={lucaCompanyOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Şirket Seçiniz"
                                    filter
                                    className={classNames({ 'p-invalid': !selectedCompanyCode, 'w-full': true })}
                                    emptyMessage="Şirket Bulunamadı"
                                    disabled={isAuthenticatedWithVkn}
                                />
                                {!selectedCompanyCode && (
                                    <small className="p-error absolute left-0" style={{ bottom: '-18px' }}>
                                        Şirket seçilmedi
                                    </small>
                                )}
                            </div>

                            <div className="w-14rem relative">
                                <label className="mb-2 block">Luca Dönemi</label>
                                <Dropdown
                                    value={selectedPeriodId}
                                    onChange={(event) => {
                                        setSelectedPeriodId(event.value);
                                        resetInventorySelection();
                                    }}
                                    options={periodOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Dönem Seçiniz"
                                    filter
                                    className={classNames({ 'p-invalid': !selectedPeriodId, 'w-full': true })}
                                    emptyMessage="Dönem Bulunamadı"
                                    disabled={!selectedCompanyCode}
                                />
                                {!selectedPeriodId && (
                                    <small className="p-error absolute left-0" style={{ bottom: '-18px' }}>
                                        Dönem seçilmedi
                                    </small>
                                )}
                            </div>

                            {inventoryPeriods.length > 0 && (
                                <div className="w-24rem">
                                    <label className="mb-2 block">Envanter Dönemi</label>
                                    <Dropdown
                                        value={selectedInventoryPeriodId}
                                        onChange={handleInventoryPeriodChange}
                                        options={inventoryPeriodOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Envanter dönemi seçiniz"
                                        filter
                                        className="w-full"
                                        emptyMessage="Envanter dönemi bulunamadı"
                                        disabled={inventoryLoading}
                                    />
                                    {selectedInventoryPeriodInfo}
                                </div>
                            )}

                            <Button icon="pi pi-list" label="Envanter Dönemlerini Getir" className="mt-4" onClick={handleLoadInventoryPeriods} disabled={!selectedCompanyCode || !selectedPeriodId} loading={inventoryLoading} />
                        </div>

                        <Button icon="pi pi-send" label="Gönder" severity="success" className="text-white mt-4" disabled={!sendEnabled} onClick={() => setSendDialogVisible(true)} loading={sendLoading} />
                    </div>

                    <div className="card">
                        <DataTable
                            value={inventoryDetailRows}
                            dataKey="_rowKey"
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="{totalRecords} kayıttan {first} ila {last} arası gösteriliyor"
                            globalFilter={inventoryDetailGlobalFilter}
                            header={detailHeader}
                            emptyMessage="Envanter kaydı bulunamadı"
                            loading={inventoryDetailLoading}
                            expandedRows={inventoryExpandedRows}
                            onRowToggle={(event) => setInventoryExpandedRows(event.data)}
                            rowExpansionTemplate={detailExpansionTemplate}
                            className="p-datatable-gridlines"
                        >
                            <Column expander style={{ width: '4rem' }} />
                            <Column header="Kebir" body={inventoryInfoTemplate} sortable sortField="kebir" style={{ minWidth: '18rem' }} />
                            <Column field="tarih" header="Tarih" sortable style={{ minWidth: '9rem' }} />
                            <Column header="Durum" body={statusTemplate} sortable sortField="durumAciklama" style={{ minWidth: '10rem' }} />
                            <Column header="Yöntem" body={inventoryMethodTemplate} sortable sortField="yontemAciklama" style={{ minWidth: '16rem' }} />
                            <Column header="Tutar" body={(rowData) => amountTemplate(rowData.detaylar?.[0] || {})} style={{ minWidth: '12rem' }} />
                            <Column field="kullanici" header="Kullanıcı" sortable style={{ minWidth: '8rem' }} />
                            <Column field="eklemeTarihi" header="Ekleme Tarihi" sortable style={{ minWidth: '12rem' }} />
                        </DataTable>
                    </div>

                    <Dialog
                        visible={sendDialogVisible}
                        onHide={!sendLoading ? () => setSendDialogVisible(false) : () => {}}
                        header="Envanter Gönder"
                        modal
                        style={{ width: '660px' }}
                        footer={
                            <div className="flex justify-content-end flex-wrap gap-2">
                                <Button
                                    icon="pi pi-check"
                                    label={sendLoading ? 'Envanter Gönderiliyor...' : 'Envanter Defterini Taslaklara Kaydet'}
                                    className="text-white"
                                    onClick={() => handleSubmitSend(0)}
                                    disabled={!enteredBy.trim() || sendLoading}
                                    loading={sendLoading}
                                />
                                <Button
                                    icon="pi pi-check"
                                    label={sendLoading ? 'Envanter Gönderiliyor...' : 'Envanter Defterini Oluştur ve Portale Gönder'}
                                    className="text-white"
                                    onClick={() => handleSubmitSend(1)}
                                    disabled={!enteredBy.trim() || sendLoading}
                                    loading={sendLoading}
                                />
                            </div>
                        }
                    >
                        <div className="flex flex-column gap-4 pt-2">
                            <div className="surface-100 border-round p-3 text-sm">
                                <div className="font-semibold">{selectedInventoryPeriod?.aciklama}</div>
                                <div className="text-color-secondary mt-1">{inventoryDetails.length} kayıt gönderime hazırlanıyor.</div>
                            </div>
                            <div className="flex flex-column gap-2">
                                <label className="font-medium">
                                    Gönderen Kişi <span className="text-pink-500 font-bold">*</span>
                                </label>
                                <InputText value={enteredBy} onChange={(event) => setEnteredBy(event.target.value)} placeholder="Gönderen kişi giriniz" autoFocus />
                            </div>
                        </div>
                    </Dialog>
                </>
            )}
        </div>
    );
}
